// ✅ UPDATED: Added BranchCode + SemesterCode dropdowns (like "SubCampus style")
// - Campus -> Branch -> Semester -> Class
// - Class list filters based on selected Branch + Semester
// - Resets dependent selections on change
// - Theme kept, attendance logic kept (absentees only)

import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "./config";

const AttendanceForm = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const SURFACE_BORDER = "rgba(25,118,210,0.25)";
  const ROW_ALT = "rgba(25,118,210,0.06)";

  const navigate = useNavigate();

  // ✅ Added BranchCode + SemesterCode into formData
  const [formData, setFormData] = useState({
    campus: "",
    branchCode: "",
    semesterCode: "",
    class: "",
    teacherName: "",
    topicsCovered: "",
    isTopicCompleted: false,
  });

  const [campusList, setCampusList] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);

  // 👇 Only track absentees
  const [absentSet, setAbsentSet] = useState(new Set());

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [stats, setStats] = useState({ total: 0, present: 0, absent: 0 });

  const [semesterFromClassDetails, setSemesterFromClassDetails] = useState(null); // from dataClassDetails
  const [loginUsername, setLoginUsername] = useState("");
  const [logincampusCode, setCampusCode] = useState("");
  const token = localStorage.getItem("auth");

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode || "");
        const uname = decodedToken.username || decodedToken.loginUsername || "";
        setLoginUsername(uname);
      } catch (err) {
        console.error("Error decoding token:", err);
        setCampusCode("");
        setLoginUsername("");
      }
    }
  }, [token]);

  // Fetch campuses (same)
  useEffect(() => {
    const fetchCampuses = async () => {
      if (!logincampusCode) return;
      try {
        const resp = await axios.get(
          `${API_URL}/api/GetCampuses?CampusCode=${encodeURIComponent(logincampusCode)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCampusList(resp.data);
      } catch (err) {
        console.error("Error fetching campuses:", err);
        setError("Failed to fetch campuses.");
      }
    };
    fetchCampuses();
  }, [logincampusCode, token]);

  // Fetch classes for campus
  useEffect(() => {
    const fetchClasses = async () => {
      if (!formData.campus) {
        setClasses([]);
        return;
      }
      try {
        const resp = await axios.get(`${API_URL}/api/dataClasses?campusID=${formData.campus}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClasses(resp.data || []);
      } catch (err) {
        console.error("Error fetching classes:", err);
        setError("Failed to fetch classes.");
      }
    };
    fetchClasses();
  }, [formData.campus, token]);

  // ✅ Build Branch + Semester dropdown options from classes
  const branchOptions = useMemo(() => {
    const arr = (classes || [])
      .map((c) => c.BranchCode)
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== "");
    return Array.from(new Set(arr)).sort((a, b) => String(a).localeCompare(String(b)));
  }, [classes]);

  const semesterOptions = useMemo(() => {
    const arr = (classes || [])
      .map((c) => c.SemesterCode ?? c.Semester ?? c.semesterCode ?? c.semester)
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
      .map((v) => String(v));
    return Array.from(new Set(arr)).sort((a, b) => String(a).localeCompare(String(b)));
  }, [classes]);

  // ✅ Filtered class list based on Branch + Semester
  const filteredClasses = useMemo(() => {
    return (classes || []).filter((cls) => {
      const okBranch = !formData.branchCode || String(cls.BranchCode || "") === String(formData.branchCode);

      const clsSem = cls.SemesterCode ?? cls.Semester ?? cls.semesterCode ?? cls.semester;
      const okSem =
        !formData.semesterCode || String(clsSem ?? "") === String(formData.semesterCode);

      return okBranch && okSem;
    });
  }, [classes, formData.branchCode, formData.semesterCode]);

  // Fetch class details (teacher + students) and init absentees (OnLeave => Absent)
  useEffect(() => {
    const fetchClassDetails = async () => {
      if (!formData.class) {
        setStudents([]);
        setFormData((prev) => ({ ...prev, teacherName: "" }));
        setAbsentSet(new Set());
        setSemesterFromClassDetails(null);
        return;
      }
      try {
        const resp = await axios.get(`${API_URL}/api/dataClassDetails?classID=${formData.class}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { teacherName, students } = resp.data;

        const sem = [
          ...new Set((students || []).map((s) => s.Semester ?? s.semester).filter(Boolean)),
        ];

        setFormData((prev) => ({ ...prev, teacherName }));
        setSemesterFromClassDetails(sem.length ? String(sem[0]) : null);
        setStudents(students || []);

        // Default absentees to those on leave
        const initAbsent = new Set();
        (students || []).forEach((s) => {
          if (s.OnLeave && String(s.OnLeave).trim() !== "") {
            initAbsent.add(s.StudentKey);
          }
        });
        setAbsentSet(initAbsent);
      } catch (err) {
        console.error("Error fetching class details:", err);
        setError("Failed to fetch class details.");
      }
    };
    fetchClassDetails();
  }, [formData.class, token]);

  // Stats from absentSet
  useEffect(() => {
    const total = students.length;
    const absent = absentSet.size;
    const present = total - absent;
    setStats({ total, present, absent });
  }, [absentSet, students]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      if (name === "campus") {
        return {
          ...prev,
          campus: value,
          branchCode: "",
          semesterCode: "",
          class: "",
          teacherName: "",
        };
      }

      if (name === "branchCode") {
        return {
          ...prev,
          branchCode: value,
          semesterCode: "",
          class: "",
          teacherName: "",
        };
      }

      if (name === "semesterCode") {
        return {
          ...prev,
          semesterCode: value,
          class: "",
          teacherName: "",
        };
      }

      if (name === "class") {
        return {
          ...prev,
          class: value,
          teacherName: "",
        };
      }

      return { ...prev, [name]: finalValue };
    });

    if (
      name === "campus" ||
      name === "branchCode" ||
      name === "semesterCode" ||
      name === "class"
    ) {
      setStudents([]);
      setAbsentSet(new Set());
      setError("");
      setSuccess("");
    }
  };

  // Toggle attendance by editing absentSet
  const handleAttendanceToggle = (StudentKey, status) => {
    setAbsentSet((prev) => {
      const next = new Set(prev);
      if (status === "Absent") next.add(StudentKey);
      else next.delete(StudentKey);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.campus || !formData.class) {
      setError("Please fill in all required fields.");
      return;
    }

    // ✅ Prefer selected semesterCode; fallback to derived semester from class details
    const finalSemester = formData.semesterCode || semesterFromClassDetails || null;

    const payload = {
      subCampusID: Number(formData.campus),
      classID: Number(formData.class),
      recordedBy: loginUsername,
      semester: finalSemester,
      absentees: Array.from(absentSet),
      topicsCovered: formData.topicsCovered,
      isTopicCompleted: formData.isTopicCompleted ? 1 : 0,
    };    

    try {
      const resp = await axios.post(`${API_URL}/api/submitAbsences`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 201) {
        setSuccess("Attendance (absences) submitted successfully!");
        setError("");
        setFormData({ campus: "", branchCode: "", semesterCode: "", class: "", teacherName: "", topicsCovered: "", isTopicCompleted: false, });
        setStudents([]);
        setAbsentSet(new Set());
        setSemesterFromClassDetails(null);
      }
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setError("Attendance already recorded for today.");
      } else {
        console.error("Error submitting attendance:", err);
        setError("Failed to submit attendance.");
      }
    }
  };

  // ✅ common sx
  const surfacePaperSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${SURFACE_BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: SURFACE_BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(25,118,210,0.45)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(240,120,40,0.75)",
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": { borderColor: "rgba(25,118,210,0.55)", backgroundColor: "rgba(25,118,210,0.04)" },
  };

  const rowSx = (i) => ({
    backgroundColor: i % 2 ? ROW_ALT : "white",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.04)" },
  });

  return (
    <Box sx={{ p: 2, backgroundColor: BG }}>
      <Paper
        elevation={5}
        sx={{
          mx: "auto",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        {/* ✅ Header bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, lineHeight: 1.2 }}>
              Attendance Form
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Mark absentees and submit today’s attendance
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button variant="contained" type="submit" form="attendance-form" sx={primaryBtnSx}>
              Submit
            </Button>
          </Stack>
        </Box>

        {/* ✅ Body */}
        <Box sx={{ p: 2, backgroundColor: BG }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          {success ? (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {success}
            </Alert>
          ) : null}

          {/* Filters */}
          <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Select Class
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <form id="attendance-form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Campus */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Campus</InputLabel>
                    <Select
                      name="campus"
                      label="Campus"
                      value={formData.campus}
                      onChange={handleChange}
                      required
                    >
                      {campusList.map((c) => (
                        <MenuItem key={c.CampusID} value={c.CampusID}>
                          {c.Campus}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ✅ NEW: BranchCode */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" sx={inputSx} disabled={!formData.campus}>
                    <InputLabel>Branch</InputLabel>
                    <Select
                      name="branchCode"
                      label="Branch"
                      value={formData.branchCode}
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>All Branches</em>
                      </MenuItem>
                      {branchOptions.map((b) => (
                        <MenuItem key={b} value={b}>
                          {b}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ✅ NEW: SemesterCode */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={inputSx}
                    disabled={!formData.campus || !semesterOptions.length}
                  >
                    <InputLabel>Semester</InputLabel>
                    <Select
                      name="semesterCode"
                      label="Semester"
                      value={formData.semesterCode}
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>All Semesters</em>
                      </MenuItem>
                      {semesterOptions.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Class */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" sx={inputSx} disabled={!formData.campus}>
                    <InputLabel>Class</InputLabel>
                    <Select
                      name="class"
                      label="Class"
                      value={formData.class}
                      onChange={handleChange}
                      required
                    >
                      {filteredClasses.map((cls) => (
                        <MenuItem key={cls.ClassID} value={cls.ClassID}>
                           {cls.Regulation} {cls.ClassName} (Section: {cls.Section})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Teacher */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Teacher Name"
                    value={formData.teacherName}
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={10}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    size="small"
                    name="topicsCovered"
                    label="Topics Covered in this Class"
                    placeholder="Example: Binary Search Trees, insertion and traversal"
                    value={formData.topicsCovered}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isTopicCompleted"
                        checked={formData.isTopicCompleted}
                        onChange={handleChange}
                      />
                    }
                    label="Is Topic completed ?"
                  />
                </Grid>
              </Grid>
            </form>
          </Paper>

          {/* Students Table */}
          {students.length > 0 ? (
            <Paper variant="outlined" sx={{ ...surfacePaperSx, overflow: "hidden" }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  borderBottom: "1px solid rgba(25,118,210,0.12)",
                  backgroundColor: "rgba(25,118,210,0.03)",
                }}
              >
                <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                  Students
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip label={`Total: ${stats.total}`} size="small" />
                  <Chip label={`Present: ${stats.present}`} size="small" />
                  <Chip label={`Absent: ${stats.absent}`} size="small" sx={{ color: THEME_ORANGE }} />
                </Stack>
              </Box>

              <TableContainer component={Box} sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      {["#", "StudentID", "StudentName", "OnLeave", "Attendance"].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontWeight: 900,
                            color: "white",
                            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
                            borderBottom: "1px solid rgba(25,118,210,0.20)",
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {students.map((student, index) => {
                      const isOnLeave = student.OnLeave && String(student.OnLeave).trim() !== "";
                      const isAbsent = absentSet.has(student.StudentKey);

                      return (
                        <TableRow key={student.StudentKey} sx={rowSx(index)}>
                          <TableCell sx={{ width: 60 }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 34,
                                height: 24,
                                px: 1,
                                borderRadius: 2,
                                fontWeight: 900,
                                color: "white",
                                backgroundColor: THEME_ORANGE,
                              }}
                            >
                              {index + 1}
                            </Box>
                          </TableCell>

                          <TableCell>{student.StudentID}</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>{student.StudentName}</TableCell>

                          <TableCell sx={{ color: isOnLeave ? "error.main" : "inherit" }}>
                            {student.OnLeave}
                          </TableCell>

                          <TableCell sx={{ minWidth: 180 }}>
                            <FormControl fullWidth size="small" sx={inputSx}>
                              <Select
                                value={isAbsent ? "Absent" : "Present"}
                                onChange={(e) => handleAttendanceToggle(student.StudentKey, e.target.value)}
                              >
                                <MenuItem value="Present">Present</MenuItem>
                                <MenuItem value="Absent">Absent</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ px: 2, py: 1.25, borderTop: "1px solid rgba(25,118,210,0.12)" }}>
                <Typography sx={{ opacity: 0.85 }}>
                  <strong>Total Students:</strong> {stats.total} | <strong>Present:</strong>{" "}
                  {stats.present} | <strong>Absent:</strong> {stats.absent}
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2 }}>
              <Typography sx={{ opacity: 0.75 }}>
                Select a campus (and optionally branch/semester) and class to load students.
              </Typography>
            </Paper>
          )}

          {/* Bottom submit */}
          {students.length > 0 ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="contained" type="submit" form="attendance-form" sx={primaryBtnSx}>
                Submit Attendance
              </Button>
            </Box>
          ) : null}
        </Box>
      </Paper>
    </Box>
  );
};

export default AttendanceForm;
